import type { CompanyDocument } from '@pn/core/domain/intel/document';
import type {
  CompanyBOENewsArticle,
  CompanyKPI,
  ExtraCompanyInfo,
} from '@pn/core/domain/intel/intel';
import { isNil, reverse } from 'lodash-es';
import type {
  ApiBOEReportArticle,
  ApiCompanyDocument,
  ApiCompanyKPI,
  ApiExtraCompanyInfo,
} from './types';

export const apiCompanyInfoMapper = {
  toDomainCompanyKPI: (apiIntelCompanyKPI: ApiCompanyKPI): CompanyKPI => {
    if (isNil(apiIntelCompanyKPI)) {
      throw new Error('apiIntelCompanyKPI is not a valid object');
    }

    return {
      id: apiIntelCompanyKPI.company_id,
      companyId: apiIntelCompanyKPI.company_id,
      wellLicensesCount: apiIntelCompanyKPI.well_licenses_count,
      producingWellsCount: apiIntelCompanyKPI.producing_wells_count,
      suspendedWellsCount: apiIntelCompanyKPI.suspended_wells_count,
      activeWellsCount: apiIntelCompanyKPI.active_wells_count,
      majorProvinces: apiIntelCompanyKPI.major_provinces,
      topFiveProjectedFormations:
        apiIntelCompanyKPI.top_five_projected_formations ?? [],
      wellLicensesIssuedLastThreeMonthsCount:
        apiIntelCompanyKPI.well_licenses_issued_last_three_months_count,
      wellLicensesIssuedLastTwelveMonthsCount:
        apiIntelCompanyKPI.well_licenses_issued_last_twelve_months_count,
      wellLicensesDrilledLastThreeMonthsCount:
        apiIntelCompanyKPI.well_licenses_spudded_last_three_months_count,
      wellLicensesDrilledLastTwelveMonthsCount:
        apiIntelCompanyKPI.well_licenses_spudded_last_twelve_months_count,
      recentMonthGrossOilBBLPerDay:
        apiIntelCompanyKPI.recent_month_gross_oil_bbl_per_day,
      recentMonthGrossGasMCFPerDay:
        apiIntelCompanyKPI.recent_month_gross_gas_mmcf_per_day,
      recentMonthGrossBOEPerDay:
        apiIntelCompanyKPI.recent_month_gross_boe_per_day,
      percentChangeSixMonthsBOEPerDay:
        apiIntelCompanyKPI.percent_change_six_months_boe_per_day,
    };
  },
  toDomainCompanyInfo: (apiCompany: ApiExtraCompanyInfo): ExtraCompanyInfo => {
    if (isNil(apiCompany)) {
      throw new Error('apiCompanyDetail is not a valid object');
    }

    return {
      id: apiCompany.id, // TODO: this is a hack, we need to get the company id from the api
      name: apiCompany.companyName,
      stockSymbol: apiCompany.symbol,
      image: apiCompany.image,
      website: apiCompany.website,
      description: apiCompany.description,
      address: apiCompany.address,
      city: apiCompany.city,
      province: apiCompany.state,
      zip: apiCompany.zip,
      industry: apiCompany.industry,
      ipoDate: apiCompany.ipoDate,
      exchange: apiCompany.exchange,
      fullTimeEmployees: apiCompany.fullTimeEmployees,
      executives: reverse(apiCompany.executives).map((exec) => {
        return { ...exec, id: exec.name };
      }),
      dailyHistoricalStockPrices: apiCompany.historical,
      quarterlyFinancialStatements: apiCompany.quarterlyFinancialStatements.map(
        (qfs) => {
          return {
            ...qfs,
            yearAndQuarter: parseInt(`${qfs.calendarYear}${qfs.period[1]}`),
          };
        }
      ),
    };
  },
  toDomainCompanyDocument: (
    apiIntelDocument: ApiCompanyDocument
  ): CompanyDocument => {
    if (isNil(apiIntelDocument)) {
      throw new Error('apiDomainDocument is not valid');
    }
    const year = parseInt(apiIntelDocument.year);
    return {
      id: apiIntelDocument.id,
      name: apiIntelDocument.file_name,
      type: apiIntelDocument.document_type,
      year,
      period: apiIntelDocument.period,
      date: getDateFromYearAndPeriod(
        year,
        apiIntelDocument.period
      ).toISOString(),
      tags: [],
    };
  },
  toDomainBOEReportArticle: (
    apiIntelBOEReportArticle: ApiBOEReportArticle
  ): CompanyBOENewsArticle => {
    if (isNil(apiIntelBOEReportArticle)) {
      throw new Error('apiIntelBOEReportArticle is not valid');
    }

    return {
      id: '',
      companyId: '',
      headline: apiIntelBOEReportArticle.post_title,
      date: apiIntelBOEReportArticle.post_date_utc,
      link: apiIntelBOEReportArticle.url,
    };
  },
};

function getDateFromYearAndPeriod(year: number, period: string): Date {
  switch (period) {
    case 'YE': // year end
      return new Date(year, 11, 31);
    case 'Q1':
      return new Date(year, 3, 1);
    case 'Q2':
      return new Date(year, 6, 1);
    case 'Q3':
      return new Date(year, 9, 1);
    case 'Q4':
      return new Date(year, 12, 1);
    case 'Jan':
      return new Date(year, 1, 1);
    case 'Feb':
      return new Date(year, 2, 1);
    case 'Mar':
      return new Date(year, 3, 1);
    case 'Apr':
      return new Date(year, 4, 1);
    case 'May':
      return new Date(year, 5, 1);
    case 'Jun':
      return new Date(year, 6, 1);
    case 'Jul':
      return new Date(year, 7, 1);
    case 'Aug':
      return new Date(year, 8, 1);
    case 'Sept': // TODO: fix this on the backend, there should be only one 'Sep'
    case 'Sep':
      return new Date(year, 9, 1);
    case 'Oct':
      return new Date(year, 10, 1);
    case 'Nov':
      return new Date(year, 11, 1);
    case 'Dec':
      return new Date(year, 12, 1);
    default:
      return new Date();
  }
}
